/**
 * Group a variant's steps into day phases (#919).
 *
 * `recipe_steps.advance_days` says how many days before the meal a step is
 * performed. A flat numbered list hides that entirely: the Bougie Bánh Mì put a
 * 480-minute overnight cure at step 4 of 12, so a cook opening it at 18:00 to
 * make dinner met the 8-hour wait only on reaching it.
 *
 * The grouping is a straight run-length pass, NOT a `group by advanceDays`.
 * Steps are chronological and lead time is non-increasing (the coherence
 * validator enforces that on AI output), so consecutive runs are the honest
 * reading — and if a legacy row ever does go 1 → 0 → 1, this renders three
 * groups in step order rather than silently reordering the cook's steps to make
 * the data look consistent.
 */

export interface StepPhase<T> {
  /** Days before the meal every step in this run is performed. */
  advanceDays: number;
  steps: T[];
}

/**
 * Split steps into consecutive same-lead-time runs, preserving order.
 *
 * Returns a SINGLE phase when every step shares one lead time — which is the
 * overwhelming majority of recipes, and the signal callers use to render
 * exactly the flat list they rendered before this column existed.
 */
export function groupStepsByPhase<T extends { advanceDays?: number | null }>(
  steps: readonly T[],
): Array<StepPhase<T>> {
  const phases: Array<StepPhase<T>> = [];
  for (const step of steps) {
    const advanceDays = step.advanceDays ?? 0;
    const current = phases[phases.length - 1];
    if (current && current.advanceDays === advanceDays) {
      current.steps.push(step);
    } else {
      phases.push({ advanceDays, steps: [step] });
    }
  }
  return phases;
}

/** True when any step is performed before the day of the meal. */
export function hasAdvancePrep<T extends { advanceDays?: number | null }>(
  steps: readonly T[],
): boolean {
  return steps.some((s) => (s.advanceDays ?? 0) > 0);
}

/**
 * The largest lead time in the list — how many days before the meal the cook
 * has to start. 0 when everything happens on the day.
 */
export function maxAdvanceDays<T extends { advanceDays?: number | null }>(
  steps: readonly T[],
): number {
  return steps.reduce((max, s) => Math.max(max, s.advanceDays ?? 0), 0);
}
